/**
 * ReadModeAnnotationOverlay — "Anmerken" UI for the read-mode view.
 *
 * Unlike AnnotationOverlay (which speaks ProseMirror selection coords),
 * this overlay listens to the browser's native window.getSelection() so
 * the user can highlight any rendered text and annotate it without
 * switching into edit-mode.
 *
 * Block resolution (2026-06-12): we don't have a `data-block-id` per
 * paragraph in the markdown-rendered HTML (markdown→HTML loses paragraph
 * metadata), so we resolve the selection back to the structured
 * paragraph list by **substring search** on the captured text. The
 * first paragraph that contains the snippet wins; offsets inside the
 * paragraph are derived from indexOf. False positives are possible only
 * when the same exact snippet appears verbatim in multiple paragraphs —
 * rare in practice, and the snippet itself is stored as anchor_quote so
 * AnnotationHighlight's fallback still finds the right span.
 *
 * Falls back to an "Edit-Mode öffnen" hint when:
 *   - selection spans multiple paragraphs
 *   - selection text can't be located in any paragraph (e.g. markdown
 *     formatting eats it — bold/italic boundaries)
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { CONTENT_API } from '../config'
import { getAccessToken, getUser } from '../auth'
import { colorOf, initials, isAgentId } from '../utils/annotationAuthors'
import { findOccurrence } from '../utils/annotationMarks'

interface StructuredParagraph {
  index: number
  text: string
  block_id?: string | null
}

interface Props {
  postId: number
  /** Live structured paragraph list from CollabReadView's Yjs mirror. */
  paragraphs: StructuredParagraph[] | null
  /** Root DOM element we listen on for selection changes. */
  readModeRoot: HTMLElement | null
  /** Called after a successful annotation create — parent may want to
   *  do nothing (Yjs WS pushes the new section in automatically) but
   *  the hook is here for symmetry with AnnotationOverlay. */
  onCreated?: () => void
  /** Normale Beiträge ohne CRDT (2026-09-30): Anker wird im Markdown-
   *  Quelltext gesucht (start_pos/end_pos), gespeichert wird über die
   *  Legacy-Annotation-API statt als Section. `paragraphs` darf dann
   *  null sein. */
  legacy?: {
    sourceText: string
    onSubmit: (anchor: { start_pos: number; end_pos: number; quote: string; context_before?: string; context_after?: string }, note: string) => Promise<boolean>
  }
  /** Zähler: jede Erhöhung öffnet den Dialog für die aktuelle Auswahl
   *  (Kontextmenü „Add Annotation"). */
  openSignal?: number
  /** Zähler: jede Erhöhung markiert die aktuelle Auswahl ohne Notiz (Taste M). */
  highlightSignal?: number
  /** Meldet das Band, das die Schreibkarte in der Randspalte belegt, damit
   *  die Karten dort ausweichen; null, wenn keine Karte offen ist. */
  onComposing?: (band: { viewportTop: number; height: number; side: 'left' | 'right' } | null) => void
}

interface ButtonPosition {
  /** Mitte der Auswahl (Viewport-Koordinaten). */
  top: number
  left: number
  /** Leiste unter statt über der Auswahl, wenn oben kein Platz ist. */
  below: boolean
}

interface ComposerPlacement {
  top: number
  left: number
  width: number
  /** `margin`: Karte in der Randspalte neben der Passage; `inline`: unter
   *  der Auswahl, wenn es keine Randspalte gibt. */
  mode: 'margin' | 'inline'
  side: 'left' | 'right'
  /** Oberkante der Auswahl; dorthin klappt die Karte, wenn unten kein Platz ist. */
  anchorTop: number
}

interface DraftRect {
  top: number
  left: number
  width: number
  height: number
}

interface ResolvedAnchor {
  block_id: string
  anchor_start: number
  anchor_end: number
  snippet: string
  /** Gerenderter Text vor und nach der Auswahl, wird mitgespeichert. */
  context_before?: string
  context_after?: string
}

/** Anker im Markdown-Quelltext (normale Beiträge): exakt, sonst
 *  Groß-/Kleinschreibung ignorierend, sonst über die ersten 30 Zeichen —
 *  dieselbe Staffel wie ShareView, nur ohne stilles 0/0. */
/**
 * Woher die Auswahl kommt: der gerenderte Text davor (für die Wahl des
 * richtigen Vorkommens) und der Absatz, in dem sie beginnt (Collab).
 * Issue #2338: „Kurs:" in einer Liste landete beim ersten „Kurs" im
 * ersten Absatz, weil nur das erste Vorkommen gesucht wurde.
 */
interface SelectionContext {
  before: string
  after: string
  blockId: string | null
}

function selectionContext(root: HTMLElement, range: Range): SelectionContext {
  let before: string
  let after: string
  try {
    const r = document.createRange()
    r.setStart(root, 0)
    r.setEnd(range.startContainer, range.startOffset)
    before = r.toString().slice(-400)
    const r2 = document.createRange()
    r2.setStart(range.endContainer, range.endOffset)
    r2.setEnd(root, root.childNodes.length)
    after = r2.toString().slice(0, 400)
  } catch {
    before = ''
    after = ''
  }
  const el = range.startContainer instanceof Element ? range.startContainer : range.startContainer.parentElement
  const block = el?.closest<HTMLElement>('[data-block-id]') ?? null
  return { before, after, blockId: block?.dataset.blockId ?? null }
}

function currentSelectionContext(root: HTMLElement | null): SelectionContext {
  const sel = window.getSelection()
  if (!root || !sel || sel.rangeCount === 0) return { before: '', after: '', blockId: null }
  return selectionContext(root, sel.getRangeAt(0))
}

/** Anker im Markdown-Quelltext (normale Beiträge): exakt, sonst
 *  Groß-/Kleinschreibung ignorierend, sonst über die ersten 30 Zeichen.
 *  Bei mehreren Vorkommen entscheidet der Text vor der Auswahl. */
function resolveLegacyAnchor(source: string, snippet: string, ctx?: SelectionContext): ResolvedAnchor | null {
  const needle = snippet.trim()
  if (!needle) return null
  const before = ctx?.before
  const after = ctx?.after
  let idx = findOccurrence(source, needle, before, false, after)
  if (idx < 0) idx = findOccurrence(source, needle.slice(0, 30), before, false, after)
  if (idx < 0) return null
  return {
    block_id: '', anchor_start: idx, anchor_end: idx + needle.length, snippet: needle,
    context_before: before?.slice(-300), context_after: after?.slice(0, 300),
  }
}

function resolveAnchor(
  paragraphs: StructuredParagraph[],
  snippet: string,
  ctx?: SelectionContext,
): ResolvedAnchor | null {
  const needle = snippet.trim()
  if (!needle) return null
  // Zuerst der Absatz, in dem die Auswahl beginnt; dann der Rest.
  const ordered = ctx?.blockId
    ? [...paragraphs.filter((p) => p.block_id === ctx.blockId), ...paragraphs.filter((p) => p.block_id !== ctx.blockId)]
    : paragraphs
  for (const p of ordered) {
    if (!p.block_id) continue
    const txt = p.text || ''
    const own = p.block_id === ctx?.blockId
    const idx = findOccurrence(txt, needle, own ? ctx?.before : undefined, false, own ? ctx?.after : undefined)
    if (idx >= 0) {
      return {
        block_id: p.block_id,
        anchor_start: idx,
        anchor_end: idx + needle.length,
        snippet: needle,
      }
    }
  }
  return null
}

export function ReadModeAnnotationOverlay({
  postId,
  paragraphs,
  readModeRoot,
  onCreated,
  legacy,
  openSignal,
  highlightSignal,
  onComposing,
}: Props) {
  const [buttonPos, setButtonPos] = useState<ButtonPosition | null>(null)
  const [anchor, setAnchor] = useState<ResolvedAnchor | null>(null)
  const [showDialog, setShowDialog] = useState(false)
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)
  // Alex 2026-10-03: „Anmerkung anfügen fühlt sich plump an". Statt
  // Knopf auf dem Text und Dialog in der Bildmitte: eine schwebende Leiste
  // über der Auswahl, und das Schreiben passiert in einer Karte in der
  // Randspalte neben der Passage. Die Passage bleibt währenddessen in der
  // eigenen Farbe markiert (Browser verwerfen die Auswahl, sobald das
  // Textfeld den Fokus bekommt).
  const rangeRef = useRef<Range | null>(null)
  // Geometrie der Auswahl zum Zeitpunkt der Leiste. Bei normalen Beiträgen
  // kann der Browser die Auswahl verlieren, bevor die Karte steht; dann
  // gilt dieser Schnappschuss, verschoben um das seitdem Gescrollte.
  const geomRef = useRef<{ rect: DOMRect; rects: DOMRect[]; scrollTop: number } | null>(null)
  const [composer, setComposer] = useState<ComposerPlacement | null>(null)
  const [draftRects, setDraftRects] = useState<DraftRect[]>([])
  const self = getUser()
  const selfName = self ? `${self.first_name || ''} ${self.last_name || ''}`.trim() || self.email : 'Du'
  const selfId = self ? `user:${self.id}` : null
  const selfColor = colorOf(selfId, selfName)

  // Listen for selection changes inside the read-mode root.
  useEffect(() => {
    if (!readModeRoot) return
    const handler = () => {
      if (showDialog) return
      const sel = window.getSelection()
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
        setButtonPos(null)
        return
      }
      const range = sel.getRangeAt(0)
      // Selection must be entirely inside the read-mode root.
      if (
        !readModeRoot.contains(range.startContainer) ||
        !readModeRoot.contains(range.endContainer)
      ) {
        setButtonPos(null)
        return
      }
      const text = sel.toString().trim()
      if (text.length < 2) {
        setButtonPos(null)
        return
      }
      const ctx = selectionContext(readModeRoot, range)
      const resolved = legacy
        ? resolveLegacyAnchor(legacy.sourceText, text, ctx)
        : paragraphs
          ? resolveAnchor(paragraphs, text, ctx)
          : null
      if (!resolved) {
        // Can't anchor — multi-paragraph or markdown ate the snippet.
        setButtonPos(null)
        setAnchor(null)
        return
      }
      const rect = range.getBoundingClientRect()
      const scroller = readModeRoot.closest('.workspace')
      geomRef.current = {
        rect,
        rects: Array.from(range.getClientRects()).filter((r) => r.width > 0 && r.height > 0),
        scrollTop: scroller ? scroller.scrollTop : window.scrollY,
      }
      setAnchor(resolved)
      const below = rect.top < 72
      setButtonPos({
        top: below ? rect.bottom + 10 : rect.top - 10,
        left: rect.left + rect.width / 2,
        below,
      })
    }
    // selectionchange is on document. Filter by root membership inside.
    document.addEventListener('selectionchange', handler)
    return () => {
      document.removeEventListener('selectionchange', handler)
    }
  }, [readModeRoot, paragraphs, showDialog, legacy])

  /** Karte neben die Passage legen: in die Randspalte, sonst unter die Auswahl. */
  const placeComposer = useCallback(() => {
    if (!readModeRoot) return
    const range = rangeRef.current
    const live = range && !range.collapsed ? range.getBoundingClientRect() : null
    let rect: DOMRect | null = live && live.height > 0 ? live : null
    let rects: Array<{ top: number; left: number; width: number; height: number }> = []
    if (rect && range) {
      rects = Array.from(range.getClientRects()).filter((r) => r.width > 0 && r.height > 0)
    } else if (geomRef.current) {
      const scroller = readModeRoot.closest('.workspace')
      const delta = (scroller ? scroller.scrollTop : window.scrollY) - geomRef.current.scrollTop
      const g = geomRef.current
      rect = new DOMRect(g.rect.left, g.rect.top - delta, g.rect.width, g.rect.height)
      rects = g.rects.map((r) => new DOMRect(r.left, r.top - delta, r.width, r.height))
    }
    if (!rect) return
    setDraftRects(rects.map((r) => ({ top: r.top, left: r.left, width: r.width, height: r.height })))
    const wrapper = readModeRoot.closest('.read-with-margin')
    const right = wrapper?.querySelector('.annot-margin--right') as HTMLElement | null
    const left = wrapper?.querySelector('.annot-margin--left') as HTMLElement | null
    const gutter = right || left
    if (gutter) {
      const g = gutter.getBoundingClientRect()
      setComposer({ top: rect.top, left: g.left, width: g.width, mode: 'margin', side: gutter === left ? 'left' : 'right', anchorTop: rect.top })
      return
    }
    const width = Math.min(380, window.innerWidth - 32)
    const x = Math.max(16, Math.min(rect.left, window.innerWidth - width - 16))
    setComposer({ top: rect.bottom + 10, left: x, width, mode: 'inline', side: 'right', anchorTop: rect.top })
  }, [readModeRoot])

  const openDialog = useCallback(() => {
    const sel = window.getSelection()
    if (sel && sel.rangeCount > 0) rangeRef.current = sel.getRangeAt(0).cloneRange()
    setShowDialog(true)
    setButtonPos(null)
    placeComposer()
  }, [placeComposer])

  // Im sichtbaren Bereich halten (#2574): Im eingebetteten Editor lag der
  // Knopf „Anmerken“ bei einer Auswahl in der letzten Zeile ausserhalb des
  // Rahmens. Reicht der Platz unter der Auswahl nicht, klappt die Karte
  // darueber; in der Randspalte wird sie nach oben geschoben.
  useLayoutEffect(() => {
    if (!showDialog || !composer || !dialogRef.current) return
    const h = dialogRef.current.offsetHeight
    const limit = window.innerHeight - 8
    let top = composer.top
    if (top + h > limit) {
      top = composer.mode === 'inline' ? composer.anchorTop - 10 - h : limit - h
    }
    top = Math.max(8, top)
    if (Math.abs(top - composer.top) > 0.5) setComposer({ ...composer, top })
  }, [showDialog, composer])

  // Das belegte Band an die Randspalte melden, sobald die Karte steht.
  useEffect(() => {
    if (!onComposing) return
    if (!showDialog || !composer || composer.mode !== 'margin') {
      onComposing(null)
      return
    }
    const h = dialogRef.current?.offsetHeight ?? 200
    onComposing({ viewportTop: composer.top, height: h, side: composer.side })
  }, [showDialog, composer, note, onComposing])

  // Scrollt oder wächst die Seite, wandert die Karte mit der Passage mit.
  useEffect(() => {
    if (!showDialog || !readModeRoot) return
    const scroller = readModeRoot.closest('.workspace') || window
    let raf = 0
    const follow = () => {
      window.cancelAnimationFrame(raf)
      raf = window.requestAnimationFrame(placeComposer)
    }
    scroller.addEventListener('scroll', follow, { passive: true })
    window.addEventListener('resize', follow)
    return () => {
      scroller.removeEventListener('scroll', follow)
      window.removeEventListener('resize', follow)
      window.cancelAnimationFrame(raf)
    }
  }, [showDialog, readModeRoot, placeComposer])

  // Kontextmenü „Add Annotation": Dialog für die aktuelle Auswahl öffnen.
  const lastSignal = useRef(openSignal ?? 0)
  useEffect(() => {
    if (openSignal == null || openSignal === lastSignal.current) return
    lastSignal.current = openSignal
    const text = window.getSelection()?.toString().trim() || ''
    const resolved = anchor
      ? anchor
      : text.length >= 2
        ? legacy
          ? resolveLegacyAnchor(legacy.sourceText, text, currentSelectionContext(readModeRoot))
          : paragraphs
            ? resolveAnchor(paragraphs, text, currentSelectionContext(readModeRoot))
            : null
        : null
    if (!resolved) return
    const sel = window.getSelection()
    if (sel && sel.rangeCount > 0) rangeRef.current = sel.getRangeAt(0).cloneRange()
    // Nicht synchron im Effekt setzen (react-hooks-Regel) — nach dem Tick.
    queueMicrotask(() => {
      setAnchor(resolved)
      setShowDialog(true)
      setButtonPos(null)
      placeComposer()
    })
  }, [openSignal, anchor, legacy, paragraphs, placeComposer])

  const closeDialog = useCallback(() => {
    setShowDialog(false)
    setNote('')
    setAnchor(null)
    setComposer(null)
    setDraftRects([])
    rangeRef.current = null
    // Clear the selection so the button doesn't pop back on next move.
    try {
      window.getSelection()?.removeAllRanges()
    } catch {
      /* ignore */
    }
  }, [])

  /** Speichert eine Anmerkung; ohne Text ist es eine Markierung (#2267). */
  const save = useCallback(async (target: NonNullable<typeof anchor>, text: string): Promise<boolean> => {
    if (legacy) {
      return legacy.onSubmit(
        { start_pos: target.anchor_start, end_pos: target.anchor_end, quote: target.snippet, context_before: target.context_before, context_after: target.context_after },
        text,
      )
    }
    const token = getAccessToken()
    if (!token) return false
    const user = getUser()
    const initiator = user
      ? `${user.first_name} ${user.last_name}`.trim() || user.email
      : 'Anon'
    try {
      const r = await fetch(
        `${CONTENT_API}/api/v1/posts/${postId}/sections/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            type: 'annotation',
            attrs: {
              initiator,
              anchor_paragraph_id: target.block_id,
              anchor_start: target.anchor_start,
              anchor_end: target.anchor_end,
              anchor_quote: target.snippet,
            },
            initial_text: `<main>${text}</main>`,
          }),
        },
      )
      if (r.ok) return true
      alert(`Fehler beim Speichern: ${r.status} ${await r.text()}`)
      return false
    } catch (e) {
      alert(`Netzwerk-Fehler: ${e instanceof Error ? e.message : String(e)}`)
      return false
    }
  }, [postId, legacy])

  // Ein Schritt (#2574): Das Fenster schliesst sofort, gespeichert wird
  // im Hintergrund. Das Anlegen einer Section dauert mit Benachrichtigung
  // der Agenten mehrere Sekunden; solange stand das Fenster mit
  // „Speichert …“ da, waehrend die Anmerkung darunter schon erschien.
  // Schlaegt das Speichern fehl, oeffnet es sich wieder mit dem Text.
  const submit = useCallback(async () => {
    if (!anchor) return
    const text = note.trim()
    if (!text) return
    const target = anchor
    const range = rangeRef.current
    closeDialog()
    if (await save(target, text)) {
      onCreated?.()
      return
    }
    rangeRef.current = range
    setAnchor(target)
    setNote(text)
    setShowDialog(true)
  }, [anchor, note, save, closeDialog, onCreated])

  /** Markieren ohne Notiz: die aktuelle Auswahl in der eigenen Farbe. */
  const highlight = useCallback(async () => {
    const text = window.getSelection()?.toString().trim() || ''
    const target = anchor
      ? anchor
      : text.length >= 2
        ? legacy
          ? resolveLegacyAnchor(legacy.sourceText, text, currentSelectionContext(readModeRoot))
          : paragraphs
            ? resolveAnchor(paragraphs, text, currentSelectionContext(readModeRoot))
            : null
        : null
    if (!target || submitting) return
    setSubmitting(true)
    try {
      if (await save(target, '')) {
        closeDialog()
        setButtonPos(null)
        onCreated?.()
      }
    } finally {
      setSubmitting(false)
    }
  }, [anchor, legacy, paragraphs, submitting, save, closeDialog, onCreated])

  // Taste M: Zähler von außen, wie openSignal.
  const lastHighlightSignal = useRef(highlightSignal ?? 0)
  useEffect(() => {
    if (highlightSignal == null || highlightSignal === lastHighlightSignal.current) return
    lastHighlightSignal.current = highlightSignal
    queueMicrotask(() => { void highlight() })
  }, [highlightSignal, highlight])

  // Close on Escape; ⌘+Enter to submit.
  useEffect(() => {
    if (!showDialog) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDialog()
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [showDialog, closeDialog, submit])

  const copySelection = () => {
    const text = window.getSelection()?.toString() || anchor?.snippet || ''
    if (text) void navigator.clipboard?.writeText(text)
    setButtonPos(null)
    try {
      window.getSelection()?.removeAllRanges()
    } catch {
      /* ignore */
    }
  }

  return (
    <>
      {buttonPos && !showDialog && (
        <div
          className={`annot-toolbar${buttonPos.below ? ' annot-toolbar--below' : ''}`}
          style={{ top: buttonPos.top, left: buttonPos.left, ['--annot-color' as string]: selfColor }}
          role="toolbar"
          aria-label="Auswahl"
          onMouseDown={(e) => {
            // Die Auswahl darf durch den Klick nicht zusammenfallen.
            e.preventDefault()
          }}
        >
          <button type="button" className="annot-toolbar__btn" onClick={() => void highlight()} title="Markieren ohne Notiz (M)">
            <span className="annot-toolbar__dot" />
            Markieren
          </button>
          <span className="annot-toolbar__sep" />
          <button type="button" className="annot-toolbar__btn" onClick={openDialog} title="Anmerkung schreiben (A)">
            Anmerken
          </button>
          <span className="annot-toolbar__sep" />
          <button type="button" className="annot-toolbar__btn" onClick={copySelection}>
            Kopieren
          </button>
        </div>
      )}

      {showDialog &&
        draftRects.map((r, i) => (
          <span
            key={i}
            className="annot-draft-mark"
            style={{ top: r.top, left: r.left, width: r.width, height: r.height, ['--annot-color' as string]: selfColor }}
          />
        ))}

      {showDialog && anchor && composer && (
        <div
          ref={dialogRef}
          className={`annot-composer annot-composer--${composer.mode} annot-composer--${composer.side}`}
          style={{ top: composer.top, left: composer.left, width: composer.width, ['--annot-color' as string]: selfColor }}
        >
          <div className="annot-card__head">
            <span className={`annot-card__avatar${isAgentId(selfId) ? ' annot-card__avatar--agent' : ''}`}>
              {initials(selfName, selfId)}
            </span>
            <strong>{selfName}</strong>
            <span className="annot-card__time">neue Anmerkung</span>
          </div>
          <div className="annot-composer__quote" title={anchor.snippet}>
            „{anchor.snippet}"
          </div>
          <textarea
            className="annot-composer__text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Was fällt dir hier auf?"
            autoFocus
            rows={3}
          />
          <div className="annot-card__actions">
            <button type="button" onClick={closeDialog}>
              Abbrechen
            </button>
            <button
              type="button"
              className="annot-card__send"
              onClick={submit}
              disabled={submitting || !note.trim()}
              title="⌘ + Enter"
            >
              {submitting ? 'Speichert …' : 'Anmerken'}
            </button>
          </div>
        </div>
      )}
    </>
  )
}
